import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NodeExpression, FieldExpression, term, toString as luceneToString } from 'lucene';
import { ActivatedRoute, Router } from '@angular/router';

import { AUTH_SERVER_URL, ORDER_SERVER_URL } from '../../config';
import { Order, characteristicFields, FieldOfOrder, AggregateOrder } from '../models/order';
import { Document } from '../../commercial-documents/models/document';
import { PaginatedResult } from '../../shared/models/paginated-result';
import { FieldOfOrderBatch, OrderBatch, orderBatchFields, orderBatchShipmentFields } from '../models/batch';
import { FieldOfWipOrder, wipOrderFields, WipOrderResult } from '../models/wip-order';
import { OrderBy, UNDERSCORE } from '../../shared/utils/constant';
import { MailContent } from '../components/card/mail-content';
import { FavoriteOrders } from '../models/favorite-orders';
import { DatePipe } from '../../shared/pipes/date.pipe';
import { UserDatacenterView } from '../../shared/models/datacenter-aggrid-view';
import { DatacenterFilterType } from '../../shared/models/datacenter-filters';
import { Characteristic, FieldOfOrderChar } from '../models/characteristic';

const GET_ORDERS_URL = `${ ORDER_SERVER_URL }/v4/orders`;
const GET_FAVORITE_ORDERS_URL = `${ ORDER_SERVER_URL }/v3/userFavoriteOrders`;
const GET_ELINK_ORDER_URL = `${ ORDER_SERVER_URL }/v1/email/eLink`;
const GET_ELINK_FILE_URL = `${ ORDER_SERVER_URL }/v1/email/fileupload`;
const GET_MILL_MAIL_URL = `${ AUTH_SERVER_URL }/v1/email/getMillMail`;
const GET_DATACENTER_ORDERS_URL = `${ ORDER_SERVER_URL }/v4/dataCenterOrders`;
const ORDER_DATACENTER_ROUTE_LINK = '/order-tracking/order/datacenter';

@Injectable({ providedIn: 'root' })
export class OrderService {
	datePipe: DatePipe;
	private readonly previousState: Record<string, unknown> | undefined;

	constructor(
		private readonly http: HttpClient,
		private readonly router: Router,
		private readonly activedRoute: ActivatedRoute,
	) {
		this.datePipe = new DatePipe();
	}

	getOrders(
		fields: readonly FieldOfOrder[],
		filter: string | undefined,
		index: number,
		limit: number,
		orderBy: OrderBy<keyof Order>[] | OrderBy<keyof Order>,
		onlyDistinct: boolean
	) {
		const url = new URL(GET_ORDERS_URL);
		const params = url.searchParams;
		params.append('fields', fields.join(','));
		if (filter) {
			params.append('filter', filter);
		}
		params.append('page', String(Math.floor(index / limit) + 1));
		params.append('limit', String(limit));

		params.append('orderBy', Array.isArray(orderBy) ? orderBy.join(',') : String(orderBy));

		params.append('distinct', String(onlyDistinct));

		return this.http.get<PaginatedResult<Order>>(url.toString());
	}

	getFavoriteOrders() {
		const url = new URL(GET_FAVORITE_ORDERS_URL);

		return this.http.get<FavoriteOrders[]>(url.toString());
	}

	saveFavoriteOrders(data: FavoriteOrders) {
		const url = new URL(`${ GET_FAVORITE_ORDERS_URL }:save`);

		return this.http.post<FavoriteOrders>(url.toString(), data);
	}

	deleteFavoriteOrders(favoriteOrderId: number) {
		const url = new URL(`${ GET_FAVORITE_ORDERS_URL }:delete`);

		return this.http.post<''>(url.toString(), favoriteOrderId);
	}

	getOrderCard(
		supplierReference: string,
		supplierItem: string,
		fields: readonly FieldOfOrder[]
	) {
		const orderId = encodeURIComponent(`${ supplierReference }/${ supplierItem }`);
		const url = new URL(`${ GET_ORDERS_URL }/${ orderId }`);
		const params = url.searchParams;
		params.append('fields', fields.join(','));
		return this.http.get<Order>(url.toString());
	}

	getOrderCharacteritics(
		supplierReference: string,
		supplierItem: string
	) {
		const orderId = encodeURIComponent(`${ supplierReference }/${ supplierItem }`);
		const url = new URL(`${ GET_ORDERS_URL }/${ orderId }/characteristics`);
		const params = url.searchParams;
		const fields: readonly FieldOfOrderChar[] = characteristicFields;
		params.append('fields', fields.join(','));

		return this.http.get<PaginatedResult<Characteristic>>(url.toString());
	}

	getOrderCount(textSearch: string) {
		const url = new URL(GET_ORDERS_URL);
		const params = url.searchParams;
		if (textSearch !== '') {
			const filter = this.createLuceneFilter(textSearch);
			params.append('filter', filter);
		}
		params.append('onlyCount', 'true');

		return this.http.get<PaginatedResult<Order>>(url.toString());
	}

	getBatchesByOrderCard(
		supplierReference: string,
		supplierItem: string,
		filter: string | undefined,
		limit: number | undefined
	) {
		const orderId = encodeURIComponent(`${ supplierReference }/${ supplierItem }`);
		const url = new URL(`${ GET_ORDERS_URL }/${ orderId }/batches`);
		const params = url.searchParams;
		if (limit) {
			params.append('limit', String(limit));
		}
		const fields: readonly FieldOfOrderBatch[] = [
			...orderBatchFields,
			...orderBatchShipmentFields
		];
		params.append('fields', fields.join(','));
		if (filter) {
			params.append('filter', filter);
		}
		return this.http.get<PaginatedResult<OrderBatch>>(url.toString());
	}

	getTotalQuantity(
		supplierReference: string,
		supplierItem: string,
		filter: string,
		distinct: boolean,
		fields: string[],
		orderBy?: string
	) {
		const orderId = encodeURIComponent(`${ supplierReference }/${ supplierItem }`);
		const url = new URL(`${ GET_ORDERS_URL }/${ orderId }/batches`);
		const params = url.searchParams;
		if (filter) {
			params.append('filter', filter);
		}
		if (distinct) {
			params.append('distinct', 'true');
		}
		if (orderBy) {
			params.append('orderBy', orderBy);
		}
		params.append('fields', fields.join(','));
		return this.http.get<PaginatedResult<OrderBatch>>(url.toString());
	}

	getWipByOrderCard(
		supplierReference: string,
		supplierItem: string
	) {
		const orderId = encodeURIComponent(`${ supplierReference }/${ supplierItem }`);
		const url = new URL(`${ GET_ORDERS_URL }/${ orderId }/wips`);
		const params = url.searchParams;
		const fields: readonly FieldOfWipOrder[] = wipOrderFields;
		params.append('fields', fields.join(','));

		return this.http.get<WipOrderResult>(url.toString());
	}

	createLuceneFilter(textSearch: string) {
		const search = `*${ term.escape(textSearch) }*`;

		const customerArticleReferenceField: FieldExpression = {
			field: 'customerArticleReference',
			term: search,
			quoted: false,
		};
		const steelgradeField: FieldExpression = {
			field: 'steelgradeDescription',
			term: search,
			quoted: false
		};
		const customerOrderNumberField = this.buildOrderNumberFilter(
			textSearch,
			'customerOrderNumber',
			'customerOrderItemNumber'
		);
		const supplierOrderNumberField = this.buildOrderNumberFilter(
			textSearch,
			'supplierOrderNumber',
			'supplierOrderItemNumber'
		);
		const supplierRFQNumberField = this.buildOrderNumberFilter(
			textSearch,
			'supplierRFQNumber',
			'supplierRFQItemNumber'
		);
		const millOrderNumberField = this.buildMillOrderNumberFilter(textSearch);
		const steelgradeCustomerNode: NodeExpression = {
			left: steelgradeField,
			operator: 'OR',
			parenthesized: false,
			right: customerOrderNumberField
		};
		const customerArticleReferenceSupplierNode: NodeExpression = {
			left: customerArticleReferenceField,
			operator: 'OR',
			parenthesized: false,
			right: supplierOrderNumberField
		};
		const customerArticleReferenceSupplierRFQNode: NodeExpression = {
			left: customerArticleReferenceSupplierNode,
			operator: 'OR',
			parenthesized: false,
			right: supplierRFQNumberField
		};

		const steelgradeCustomerNodeField: NodeExpression = {
			left: steelgradeCustomerNode,
			operator: 'OR',
			parenthesized: true,
			right: customerArticleReferenceSupplierRFQNode
		};

		const allNode: NodeExpression = {
			left: millOrderNumberField,
			operator: 'OR',
			parenthesized: true,
			right: steelgradeCustomerNodeField
		};

		return luceneToString(allNode);
	}

	buildMillOrderNumberFilter(textSearch: string) {
		const defaultCondition: FieldExpression = {
			field: 'millOrderNumber',
			term: `*${ term.escape(textSearch) }*`,
			quoted: false
		};
		if (textSearch.includes('/')) {
			const millOrderNumber = textSearch.slice(0, textSearch.lastIndexOf('/'));
			const itemNumber = textSearch.slice(textSearch.lastIndexOf('/') + 1);
			if (millOrderNumber.length > 0) {
			// Construct millOrderref conditions
				const millOrderNumberField: FieldExpression = {
					field: 'millOrderNumber',
					term: millOrderNumber.startsWith('0') ? millOrderNumber.replace(/^0+/, '')
						: millOrderNumber,
					quoted: true
				};

				const millOrderNumberFieldWithZero: FieldExpression = {
					field: 'millOrderNumber',
					term: !millOrderNumber.startsWith('0') ? `0${ millOrderNumber }`
						: millOrderNumber,
					quoted: true
				};

				const millOrderItemNumberField: FieldExpression = {
					field: 'millOrderItemNumber',
					term: itemNumber.startsWith('0') ? itemNumber.replace(/^0+/, '')
						: itemNumber,
					quoted: true
				};

				const millOrderItemNumberFieldWithZero: FieldExpression = {
					field: 'millOrderItemNumber',
					term: !itemNumber.startsWith('0') ? `0${ itemNumber }` : itemNumber,
					quoted: true
				};

				// Create NodeExpressions for millOrderref conditions
				const finalMillOrderrefField1: NodeExpression = {
					left: millOrderNumberField,
					operator: 'AND',
					right: millOrderItemNumberField,
					parenthesized: true
				};

				const finalMillOrderrefField2: NodeExpression = {
					left: millOrderNumberFieldWithZero,
					operator: 'AND',
					right: millOrderItemNumberField,
					parenthesized: true
				};

				const finalMillOrderrefField3: NodeExpression = {
					left: millOrderNumberFieldWithZero,
					operator: 'AND',
					right: millOrderItemNumberFieldWithZero,
					parenthesized: true
				};

				const finalMillOrderrefField4: NodeExpression = {
					left: millOrderNumberField,
					operator: 'AND',
					right: millOrderItemNumberFieldWithZero,
					parenthesized: true
				};

				const finalCondition1: NodeExpression = {
					left: finalMillOrderrefField1,
					operator: 'OR',
					parenthesized: false,
					right: finalMillOrderrefField2
				};

				const finalCondition2: NodeExpression = {
					left: finalMillOrderrefField3,
					operator: 'OR',
					parenthesized: false,
					right: finalMillOrderrefField4
				};

				// Constructing the final NodeExpression combining all conditions
				const finalMillOrderNumberNode: NodeExpression = {
					left: finalCondition1,
					operator: 'OR',
					parenthesized: false,
					right: finalCondition2
				};
				return finalMillOrderNumberNode;
			}
			return defaultCondition;
		}
		return defaultCondition;
	}

	buildOrderNumberFilter(textSearch: string, orderField: string, itemField: string) {
		const defaultCondition: FieldExpression = {
			field: orderField,
			term: `*${ term.escape(textSearch) }*`,
			quoted: false
		};
		if (textSearch.includes('/')) {
			const orderNumber = textSearch.slice(0, textSearch.lastIndexOf('/'));
			const itemNumber = textSearch.slice(textSearch.lastIndexOf('/') + 1);
			const isNum = /^\d+$/.test(itemNumber);
			if (isNum && orderNumber.length > 0) {
				const orderNumberField: FieldExpression = {
					field: orderField,
					term: `*${ term.escape(orderNumber) }*`,
					quoted: false
				};
				const orderItemNumberField: FieldExpression = {
					field: itemField,
					term: itemNumber,
					quoted: itemNumber === ''
				};
				const orderNumberNode: NodeExpression = {
					left: orderNumberField,
					operator: 'AND',
					parenthesized: true,
					right: orderItemNumberField
				};
				const finalOrderNode: NodeExpression = {
					left: orderNumberNode,
					operator: 'OR',
					parenthesized: true,
					right: defaultCondition
				};
				return finalOrderNode;
			}
			return defaultCondition;
		}
		return defaultCondition;
	}

	getMillMail(
		codeMill: string,
	) {
		const url = new URL(GET_MILL_MAIL_URL);
		const params = url.searchParams;
		params.append('codeMill', codeMill);
		return this.http.get(url.toString(), { responseType: 'text' });
	}

	uploadFile(file: File, hash: string) {
		const url = new URL(`${ GET_ELINK_FILE_URL }/${ hash }`);
		const form = new FormData();
		form.append('file', file);
		return this.http.post(url.toString(), form, { responseType: 'text' });
	}

	contactMill(mailContent: MailContent) {
		const url = new URL(GET_ELINK_ORDER_URL);
		const json = JSON.stringify(mailContent);
		// eslint-disable-next-line @typescript-eslint/naming-convention
		const options = { headers: { 'Content-Type': 'application/json' } };
		return this.http.post<string>(url.toString(), json, options);
	}

	getAggregatedQuantity(filter: string | undefined) {
		const url = new URL(`${ GET_ORDERS_URL }:aggregates`);
		const params = url.searchParams;
		if (filter) {
			params.append('filter', filter);
		}
		return this.http.get<AggregateOrder>(url.toString());
	}

	getDatacenterOrders(
		fields: readonly FieldOfOrder[],
		filter: string | undefined,
		index: number,
		limit: number,
		orderBy: OrderBy<keyof Order>[] | OrderBy<keyof Order> | undefined,
		onlyDistinct: boolean
	) {
		const url = new URL(GET_DATACENTER_ORDERS_URL);
		const params = url.searchParams;
		params.append('fields', fields.join(','));
		if (filter) {
			params.append('filter', filter);
		}
		params.append('page', String(Math.floor(index / limit) + 1));
		params.append('limit', String(limit));
		if (orderBy) {
			params.append('orderBy', Array.isArray(orderBy) ? orderBy.join(',') : String(orderBy));
		}

		params.append('distinct', String(onlyDistinct));

		return this.http.get<PaginatedResult<Order>>(url.toString());
	}

	checkCddWeekData(cddWeek?: string) {
		if (!cddWeek || (cddWeek === '1900-W01')) {
			return UNDERSCORE;
		}
		return this.datePipe.transform(cddWeek, 'week');
	}

	async navigation(defaultView: UserDatacenterView, routePath: string) {
		await this.router.navigate([routePath], {
			relativeTo: this.activedRoute,
			state: { ...this.previousState, view: defaultView }
		});
	}

	async orderDatacenterFilter(document: Document, defaultView: UserDatacenterView) {
		const filtersList: DatacenterFilterType[] = [];
		const orderNumberValues = [];
		if (document.type === 'IN' || document.type === 'DA') {
			for (const documentLine of document.documentLines) {
				orderNumberValues.push(
					{
						reference: documentLine.supplierOrderReference,
						item: documentLine.supplierOrderReferenceItem,
					}
				);
			}
		} else {
			orderNumberValues.push(
				{
					reference: document.supplierOrderReference,
					item: document.documentLines[0].supplierOrderReferenceItem,
				}
			);
		}
		const orderFilter: DatacenterFilterType = {
			id: 'supplierOrderNumber',
			values: orderNumberValues
		};
		filtersList.push(orderFilter);
		defaultView.filterState = filtersList;
		await this.navigation(defaultView, ORDER_DATACENTER_ROUTE_LINK);
	}
}
